import { duplicateName } from "./builder";

/**
 * DUPLICATE A TEMPLATE — the one implementation, shared by the library row and W17d's window.
 *
 * 🔴 IT WAS A FUNCTION INSIDE `Workouts.tsx` UNTIL W17d GAVE IT A SECOND CALLER, and the move is
 * the point rather than tidying. Everything below is a CORRECTION to what `clone_coach_workout`
 * does by default, measured live; a second copy of it on the window would be two surfaces that
 * agree today and disagree the first time either is edited — which is this repo's defining bug
 * class, and which `FORMAT_LABEL` and `collapsedLabel` are already here to avoid.
 *
 * ⚠️ THE CLONE IS THE DATABASE'S AND IS NEVER RE-IMPLEMENTED IN TYPESCRIPT. `clone_coach_workout`
 * is `SECURITY INVOKER`, so it runs under the coach's own RLS and can do nothing they could not
 * do by hand, and it calls `assert_clone_covers` on all four tables so it REFUSES rather than
 * copying a workout half-way the day one of them grows a column. A TypeScript clone would drop
 * seven columns silently — V7-2 measured exactly that.
 *
 * 🔴 AND THE CORRECTION, BECAUSE THE CLONE IS BUILT FOR A DIFFERENT JOB.
 * `clone_coach_workout` sets `root_workout_id = coalesce(src.root_workout_id, src.id)`, right for
 * an ASSIGNMENT copy and wrong for a template, in two measured ways:
 *   • `lastUsedByTemplate` keys on `coalesce(root_workout_id, workout_id)`, so every assignment
 *     made FROM the duplicate would be credited to the ORIGINAL and the duplicate would read
 *     "Never assigned" for as long as it existed.
 *   • `coach_assignments_one_source_per_day_idx` is unique on that same expression, so assigning
 *     the original and its duplicate to one client on one day would be refused, and
 *     `describeOutcome` would say *"already had this workout"* about one never assigned.
 * Measured live: all templates carry `root_workout_id` NULL and all client copies carry one.
 *
 * ⚠️ IT IS NOT ATOMIC, AND THE FAILURE IS NAMED RATHER THAN SWALLOWED. If the correction fails
 * the duplicate exists, under its source's name and root, and the message says so in those words.
 * It is deliberately NOT deleted on that path: deleting a `coach_workouts` row CASCADES into
 * `coach_assignments`, and a destructive act in an error handler is the shape this repo has paid
 * for — the wrong state here is bounded and nameable, a mis-fired delete is not.
 */
export type DuplicateOutcome =
  /** The copy exists, is renamed and is detached. `id` is where to send the coach. */
  | { kind: "ok"; id: string; name: string }
  /** Nothing was created. The library is unchanged. */
  | { kind: "failed"; message: string }
  /**
   * ⚠️ A THIRD STATE, NOT AN ERROR AND NOT A SUCCESS — the copy EXISTS but is still wearing its
   * source's name and root. Collapsing it into `failed` would tell a coach nothing happened while
   * a second row sat in their library; collapsing it into `ok` would send them to a builder whose
   * "last used" is about to lie. The caller says both halves out loud.
   */
  | { kind: "partial"; id: string; message: string };

/** The two calls this needs, injected — so the decision is executed by a test with no network. */
export type DuplicateDeps = {
  clone: (workoutId: string) => Promise<{ id: string | null; error: string | null }>;
  rename: (id: string, name: string) => Promise<{ error: string | null }>;
};

export async function duplicateWorkout(
  deps: DuplicateDeps,
  source: { id: string; name: string },
  existingNames: readonly string[],
  /** The copy's name — `duplicateName` (`… (copy)`) unless a caller names it otherwise (William's item 4: `Save to library`
   *  keeps the client copy's own name while it is free, `libraryName`). */
  nameFor: (existing: readonly string[], source: string) => string = duplicateName,
): Promise<DuplicateOutcome> {
  const cloned = await deps.clone(source.id);
  if (cloned.error !== null || cloned.id === null) {
    return {
      kind: "failed",
      message: `Could not duplicate ${source.name}: ${cloned.error ?? "the copy returned no id."}`,
    };
  }
  const name = nameFor(existingNames, source.name);
  const renamed = await deps.rename(cloned.id, name);
  if (renamed.error !== null) {
    return {
      kind: "partial",
      id: cloned.id,
      message:
        `A copy of ${source.name} was created, but could not be renamed or detached from the `
        + `original: ${renamed.error}. It is in your library under the same name, and until it `
        + `is renamed it will report as never assigned.`,
    };
  }
  return { kind: "ok", id: cloned.id, name };
}
